import get from 'lodash/get';
import upperFirst from 'lodash/upperFirst';

import FilterOperatorEnum from '@constants/enums/filter/operator';
import FilterTypeEnum from '@constants/enums/filter/type';

import { toDatetime, toIso8601 } from '@utils/date';
import { uuid } from '@utils/string';

import clock from '@plugins/dayjs';
import i18n from '@plugins/i18n';

/**
 * Checks if a filter is valid.
 * @param {Object} filter - The filter object.
 * @returns {Boolean} - True if the filter is valid, false otherwise.
 */
export function isValidFilter(filter) {
    if (!filter.column || !filter.operator) {
        return false;
    }

    const requiresCriteria = !getNoCriteriaOperators().includes(filter.operator);

    if (requiresCriteria && (filter.criteria === null || filter.criteria === undefined || filter.criteria === '')) {
        return false;
    }

    return true;
}

/**
 * Creates a filter panel object.
 * @param {Object} panel - The panel object to create.
 * @returns {Object} - The created filter panel object.
 */
export function createFilterPanel(panel = {}) {
    panel = {
        id: panel.id || uuid(8),
        options: createFilterPanelOptions(panel.options),
        active: panel.active || false,
        requestItems: panel.requestItems || null,
        filters: panel.filters || [],
        zIndex: panel.zIndex || 5
    };

    return panel;
}

/**
 * Creates filter panel options.
 * @param {Array} options - The options to create.
 * @returns {Array} - The created filter panel options.
 */
export function createFilterPanelOptions(options = []) {
    options = options.filter(option => !('filterable' in option) || option.filterable);

    options = options.map(option => {
        let item = {
            label: option.label || option.text || '',
            column: option.column || option.value || '',
            format: option.format || 'string',
            operators: option.operators || option.fieldType?.operators || null,
            component: option.component || null,
            options: option.options || null,
            filters: option.filters || null,
            enum: option.enum || null,
            enumA11yKey: option.enumA11yKey || null,
            enumTypes: option.enumTypes || null
        };

        if (!item.component) {
            if (item.format === 'wysiwyg') {
                item.component = `EditString`;
            } else {
                item.component = `Edit${upperFirst(item.format)}`;
            }
        }

        if (option.filterable && typeof option.filterable === 'object') {
            item = { ...item, ...option.filterable };
        }

        return item;
    });

    return options;
}

/**
 * Creates a filter query object.
 * @param {Array} filters - The filters to create the query from.
 * @returns {Object} - The filter query object.
 */
export function createFilterQuery(filters) {
    filters = filters.filter(isValidFilter);

    if (filters.length === 0) {
        return {};
    }

    let query = {};

    filters.forEach(filter => {
        let filterString = filter.operator;

        if (filter.criteria !== null && filter.criteria !== undefined) {
            if (Array.isArray(filter.criteria)) {
                filterString += `:[${filter.criteria.join(',')}]`;
            } else if (typeof filter.criteria === 'object' && 'amount' in filter.criteria) {
                filterString += `:${filter.criteria.amount}`;
            } else {
                filterString += `:${filter.criteria}`;
            }
        }

        query[filter.column] = filterString;
    });

    return query;
}

/**
 * Returns a list of operators that don't require criteria.
 * @returns Array
 */
export function getNoCriteriaOperators() {
    return Object.values(FilterOperatorEnum)
        .filter(o => !o.requiresCriteria)
        .map(o => o.value);
}

/**
 * Returns the FilterOperator object for the given key
 * @param {String} key
 * @returns {Object}
 */
export function getFilterOperator(key) {
    if (!(typeof key === 'string')) {
        return null;
    }

    key = key.toUpperCase();

    return get(FilterOperatorEnum, [key, 'value'], null);
}

/**
 * Return a FieldType object
 * @param {String} key
 * @returns {Object} FieldType
 */
export function getFieldType(key) {
    if (!(typeof key === 'string')) {
        return null;
    }

    key = key.toUpperCase();

    if (!(key in FilterTypeEnum)) {
        return null;
    }

    return FilterTypeEnum[`${key}`];
}

/**
 * Checks if an item matches a filter.
 * @param {Object} item - The item to check.
 * @param {String} key - The key to check in the item.
 * @param {String|Array} filter - The filter to apply.
 * @returns {Boolean} - True if the item matches the filter, false otherwise.
 */
export function filterMatchesItem(item, key, filter) {
    let match = true;

    if (Array.isArray(filter)) {
        match = filter.every(value => this.filterMatchesItem(item, key, value));
    } else if (key in item) {
        const hasOperator = filter.includes(':');

        let operator = FilterOperatorEnum.IS.value;
        let criteria = null;

        if (hasOperator) {
            [operator, criteria] = filter.match(/([^:]+):(.*)/).slice(1);
        } else {
            criteria = filter;
        }

        if (operator === FilterOperatorEnum.IN.value) {
            criteria = criteria.replace('[', '').replace(']', '').replace(' ', '').split(',');
        }

        const value = item[`${key}`];

        if (operator === FilterOperatorEnum.CONTAINS.value) {
            match = typeof value === 'string' && value.includes(criteria);
        } else if (operator === FilterOperatorEnum.NOT_CONTAINS.value) {
            match = typeof value === 'string' && !value.includes(criteria);
        } else if (operator === FilterOperatorEnum.STARTS_WITH.value) {
            match = typeof value === 'string' && value.startsWith(criteria);
        } else if (operator === FilterOperatorEnum.ENDS_WITH.value) {
            match = typeof value === 'string' && value.endsWith(criteria);
        } else if (operator === FilterOperatorEnum.IS.value) {
            match = value === criteria;
            if (!match && clock(value).isValid() && clock(criteria).isValid()) {
                match = clock(value).startOf('day').isSame(clock(criteria).startOf('day'));
            }
        } else if (operator === FilterOperatorEnum.IS_NOT.value) {
            match = value !== criteria;
        } else if (operator === FilterOperatorEnum.EMPTY.value) {
            match =
                value === null ||
                value === undefined ||
                ((typeof value === 'string' || Array.isArray(value)) && !value.length) ||
                (typeof value === 'object' && !Object.keys(value).length);
        } else if (operator === FilterOperatorEnum.NOT_EMPTY.value) {
            match =
                value !== null &&
                value !== undefined &&
                (typeof value === 'string' || Array.isArray(value)) &&
                value.length > 0 &&
                typeof value === 'object' &&
                Object.keys(value).length > 0;
        } else if (operator === FilterOperatorEnum.LESS_THAN_OR_IS.value) {
            match = typeof value === 'number' && value <= criteria;
        } else if (operator === FilterOperatorEnum.MORE_THAN_OR_IS.value) {
            match = typeof value === 'number' && value >= criteria;
        } else if (operator === FilterOperatorEnum.IS_BEFORE.value) {
            match = typeof value === 'string' && clock(toIso8601(value)).isBefore(clock(toIso8601(criteria))) === true;
        } else if (operator === FilterOperatorEnum.IS_AFTER.value) {
            match = typeof value === 'string' && clock(toIso8601(value)).isAfter(clock(toIso8601(criteria))) === true;
        } else if (operator === FilterOperatorEnum.IN.value) {
            match = Array.isArray(criteria) && criteria.includes(value);
        }

        if (!match) {
            console.warn('Match not found:', { key, value, filter });
        }
    } else {
        match = false;
    }

    return match;
}

/**
 * Converts a filter object into a human-readable text string.
 * @param {Object} filter - The filter object containing operator and criteria.
 * @param {Object} option - The option object describing the filterable field.
 * @returns {String|null} The human-readable filter string, or null if invalid.
 */

export function filterToText(filter, option = null) {
    if (!option) {
        return null;
    }

    const operator = option.operators.find(op => op.value === filter.operator);

    if (!operator) {
        return null;
    }

    const requiresCriteria = !getNoCriteriaOperators().includes(filter.operator);

    let written = [];

    written.push(`${option.label} `);

    if (operator.value === 'in') {
        written.push(i18n.t('is_either').toLowerCase());
    } else if (operator.value === 'not_in') {
        written.push(i18n.t('is_not').toLowerCase());
    } else {
        written.push(`${operator.text.toLowerCase()}`);
    }

    if (requiresCriteria) {
        let criteria = filter.criteria;

        if (Array.isArray(criteria)) {
            const criteriaString = criteria.map(c => `"${c}"`).join(', ');
            written.push(criteriaString.replace(/,([^,]*)$/, ' ' + i18n.t('or').toLowerCase() + '$1'));
        } else if (requiresCriteria) {
            if (filter.data && filter.data.name) {
                criteria = filter.data.name;
            }
            let criteriaText = '';

            if (option.format === 'money') {
                criteriaText = `${criteria !== null ? criteria.amount : ''}`;
            } else if (option.format === 'boolean') {
                criteriaText = `${criteria ? 'true' : 'false'}`;
            } else if (option.format === 'date') {
                criteriaText = toDatetime(criteria, 'DD/MM/YYYY');
            } else if (option.format === 'datetime') {
                criteriaText = toDatetime(criteria, 'DD/MM/YYYY');
            } else if (option.format === 'enum' && option.enumA11yKey) {
                criteriaText = `${criteria !== null ? i18n.t(option.enumA11yKey + '.' + criteria) : ''}`;
            } else {
                criteriaText = `"${criteria !== null ? criteria : ''}"`;
            }

            if (option.prependText) {
                criteriaText = `${option.prependText} ${criteriaText}`;
            }

            if (option.appendText) {
                criteriaText += ` ${option.appendText}`;
            }

            written.push(criteriaText);
        }
    }

    return written.join(' ');
}
